import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Send, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { SiteFooter } from "@/components/SiteFooter";
import PageMeta from "@/components/PageMeta";

const Contact = () => {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim().length < 10) {
      toast({
        title: "Message too short",
        description: "Please give us at least a sentence so we can help.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("contact_messages").insert({
      name: name.trim(),
      email: email.trim(),
      subject: subject.trim() || null,
      message: message.trim(),
    });
    setSubmitting(false);

    if (error) {
      toast({
        title: "Couldn't send your message",
        description: "Please try again, or email us directly at hello@gotocoin.au.",
        variant: "destructive",
      });
      return;
    }
    setSent(true);
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <PageMeta
        title="Contact GoToCoins"
        description="Get in touch with GoToCoins — questions about TOSS scores, corrections to research, media enquiries, privacy requests and partnership contact for Australian crypto traders."
        path="/contact"
      />
      <main className="flex-1 container mx-auto px-4 py-10 max-w-2xl">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <header className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Mail className="w-6 h-6 text-primary" />
            <h1 className="text-3xl font-bold tracking-tight">Contact</h1>
          </div>
          <p className="text-muted-foreground">
            Corrections, data errors, privacy requests, media or partnership enquiries — send them
            here. We read everything and usually reply within a few business days.
          </p>
        </header>

        {sent ? (
          <Card className="border-success/30 bg-success/5">
            <CardContent className="py-8 text-center space-y-3">
              <CheckCircle2 className="w-10 h-10 text-success mx-auto" />
              <h2 className="text-lg font-semibold">Message sent</h2>
              <p className="text-sm text-muted-foreground">
                Thanks {name.split(" ")[0] || "for reaching out"} — we'll get back to you at {email}.
              </p>
              <Button variant="outline" asChild>
                <Link to="/">Back to home</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Send a message</CardTitle>
              <CardDescription>
                Prefer email? Write to{" "}
                <a href="mailto:hello@gotocoin.au" className="underline underline-offset-2">
                  hello@gotocoin.au
                </a>
                .
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={120}
                      required
                      autoComplete="name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      maxLength={254}
                      required
                      autoComplete="email"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject (optional)</Label>
                  <Input
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    maxLength={200}
                    placeholder="e.g. Data correction on a TOSS score"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={5000}
                    rows={6}
                    required
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  We use your details only to reply. See the{" "}
                  <Link to="/privacy" className="underline underline-offset-2">Privacy Policy</Link>.
                  Please don't send API keys, passwords or seed phrases.
                </p>
                <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? "Sending…" : "Send message"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

export default Contact;
